import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import SiteHeader from '../components/SiteHeader';
import { useLanguage } from '../i18n/LanguageContext';
import { SITE_NAME } from '../constants/site';
import QuizShareCard from '../components/QuizShareCard';
import PersonalProfileCard from '../components/PersonalProfileCard';

interface PublicProfile {
  nickname: string | null;
  city: string | null;
  height: number | null;
  weight: number | null;
  bodyType: string | null;
  job: string | null;
  role: string[];
  hasVerified: boolean;
  zodiac: { name: string; symbol: string } | null;
  receivedLikes?: number;
  receivedThims?: number;
  receivedTotal?: number;
}

interface PublicCard {
  id: string;
  quiz_type: string;
  variant_key: string | null;
  aspect_ratio: string;
  display_name: string | null;
  share_title: string | null;
  image_url: string;
  created_at: string;
}

interface ApiResponse {
  success: boolean;
  profile?: PublicProfile;
  cards?: PublicCard[];
  cardCount?: number;
  error?: string;
}

export default function PublicCardsPage() {
  const { recordId } = useParams<{ recordId: string }>();
  const { lang, m } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<PublicProfile | null>(null);
  const [cards, setCards] = useState<PublicCard[]>([]);
  const [modalCard, setModalCard] = useState<PublicCard | null>(null);

  useEffect(() => {
    if (!recordId) {
      setError('Thiếu mã hồ sơ');
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`/api/public/profile?recordId=${encodeURIComponent(recordId || '')}`);
        const data: ApiResponse = await res.json().catch(() => ({ success: false, error: 'Lỗi phản hồi' }));

        if (!res.ok || !data.success || !data.profile) {
          if (!cancelled) setError(data.error || m.publicCards.notFound);
          return;
        }

        if (!cancelled) {
          setProfile(data.profile);
          setCards(data.cards || []);
        }
      } catch (e) {
        if (!cancelled) setError(m.publicCards.notFound);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [recordId, m.publicCards.notFound]);

  const t = m.publicCards;

  const formatDate = (iso: string) => {
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(lang === 'vi' ? 'vi-VN' : 'en-US', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit'
      });
    } catch {
      return iso;
    }
  };

  const getTypeLabel = (type: string) => {
    if (type === 'personality') return t.cardType.personality;
    if (type === 'single') return t.cardType.single;
    if (type === 'bedroom') return 'Cấu hình phòng ngủ';
    return type;
  };

  const roleDisplay = profile?.role?.length ? profile.role.map(r => r).join(' / ') : t.roleEmpty;
  const verifiedDisplay = profile?.hasVerified ? (
    <span className="public-verified">✅ {t.verified}</span>
  ) : (
    <span className="public-not-verified">• {t.notVerified}</span>
  );

  const heightWeight = profile
    ? [profile.height ? `${profile.height} cm` : null, profile.weight ? `${profile.weight} kg` : null]
        .filter(Boolean)
        .join(' · ') || m.common.emptyValue
    : m.common.emptyValue;

  return (
    <div className="app-container public-cards-page">
      <SiteHeader>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          <div className="logo" style={{ fontSize: '1.1rem', letterSpacing: '0.12em' }}>
            {SITE_NAME}
          </div>
          <div style={{ fontSize: '0.75rem', opacity: 0.7 }}>{t.title}</div>
        </div>
      </SiteHeader>

      <div style={{ maxWidth: 980, margin: '0 auto', width: '100%' }}>
        {loading && (
          <div className="public-loading">{t.loading}</div>
        )}

        {!loading && error && (
          <div className="public-error">
            <p>{error}</p>
            <Link to="/quiz" className="primary-btn" style={{ marginTop: 12, display: 'inline-block' }}>
              {t.backToQuiz}
            </Link>
          </div>
        )}

        {!loading && !error && profile && (
          <>
            {/* Header */}
            <div className="public-header">
              <h1 className="public-title">{t.title}</h1>
              <p className="public-subtitle">{t.subtitle}</p>

              <div className="public-actions">
                <Link to="/quiz" className="public-btn primary-btn">
                  ✨ {t.backToQuiz}
                </Link>
                {recordId && (
                  <a
                    href={`/cards/${recordId}`}
                    target="_blank"
                    rel="noreferrer"
                    className="public-btn"
                    style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.2)' }}
                  >
                    Mở liên kết công khai
                  </a>
                )}
              </div>
            </div>

            {/* Thẻ thông tin (nickname + role + stats) */}
            <div className="public-info-card">
              <div className="public-info-card-head">
                <div>
                  <div className="public-info-nickname">
                    {profile.nickname ? profile.nickname : 'Người dùng'}
                  </div>
                  <div className="public-info-role">
                    {roleDisplay !== t.roleEmpty ? roleDisplay : ''}
                  </div>
                </div>
                <div className="public-info-verified">
                  {verifiedDisplay}
                </div>
              </div>

              <div className="public-info-stats">
                {profile.zodiac && (
                  <div className="public-info-stat">
                    <span className="public-info-stat-label">Cung hoàng đạo</span>
                    <span className="public-info-stat-value">{profile.zodiac.symbol} {profile.zodiac.name}</span>
                  </div>
                )}
                {profile.city && (
                  <div className="public-info-stat">
                    <span className="public-info-stat-label">Thành phố</span>
                    <span className="public-info-stat-value">{profile.city}</span>
                  </div>
                )}
                {(profile.height || profile.weight) && (
                  <div className="public-info-stat">
                    <span className="public-info-stat-label">Chiều cao / Cân nặng</span>
                    <span className="public-info-stat-value">{heightWeight}</span>
                  </div>
                )}
                {profile.bodyType && (
                  <div className="public-info-stat">
                    <span className="public-info-stat-label">Tạng người</span>
                    <span className="public-info-stat-value">{profile.bodyType}</span>
                  </div>
                )}
                {profile.job && (
                  <div className="public-info-stat">
                    <span className="public-info-stat-label">Nghề nghiệp</span>
                    <span className="public-info-stat-value">{profile.job}</span>
                  </div>
                )}

                {/* Thả tim / like nhận được - thống kê cho trang cá nhân */}
                {(profile.receivedTotal || 0) > 0 && (
                  <div className="public-info-stat public-info-stat--highlight">
                    <span className="public-info-stat-label">Thả tim nhận được</span>
                    <span className="public-info-stat-value">
                      💛 {profile.receivedThims || 0} thim
                      {profile.receivedLikes ? ` · ❤️ ${profile.receivedLikes} like` : ''}
                      <span style={{ fontSize: '0.7em', opacity: 0.7, marginLeft: 4 }}>
                        (tổng {profile.receivedTotal})
                      </span>
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Thẻ cá nhân nổi bật (profile card) — hiển thị thông tin đầy đủ hấp dẫn: avatar, role, physical, job, Gu, tìm kiếm */}
            {profile && (
              <div style={{
                width: '100%',
                maxWidth: 340,
                margin: '12px auto',
                aspectRatio: '9/16',
                overflow: 'hidden',
                borderRadius: 12,
                border: '1px solid var(--border-color)',
                background: '#0a0a0a',
                boxShadow: '0 8px 30px rgba(0,0,0,0.4)',
              }}>
                <PersonalProfileCard
                  data={{
                    nickname: profile.nickname,
                    city: profile.city,
                    height: profile.height,
                    weight: profile.weight,
                    bodyType: profile.bodyType,
                    job: profile.job,
                    role: profile.role,
                    zodiac: profile.zodiac,
                    rel: (profile as any).rel,
                    // Pull Gu from first synthetic result card if available
                    guTitle: (cards.find((c: any) => (c as any).result && (c as any).quiz_type === 'personality') as any)?.result?.shareTitle,
                    guHook: (cards.find((c: any) => (c as any).result && (c as any).quiz_type === 'personality') as any)?.result?.shareHook,
                  }}
                  displayName={profile.nickname || undefined}
                  totalLikes={profile.receivedTotal || 0}
                />
              </div>
            )}

            {/* Thẻ quiz — chỉ hiện card cuối cùng (mới nhất) của mỗi quiz. Số thẻ tăng khi thêm quiz. */}
            <div className="public-cards-section">
              <div className="public-section-head">
                <h2>Thẻ quiz gần nhất</h2>
                <span className="public-count">{cards.length} thẻ</span>
              </div>

              {cards.length === 0 ? (
                <div className="public-empty">
                  <p>{t.cardsEmpty}</p>
                  <p className="public-empty-hint">{t.cardsEmptyHint}</p>
                  <Link to="/quiz" className="primary-btn" style={{ marginTop: 16 }}>
                    Bắt đầu làm quiz
                  </Link>
                </div>
              ) : (
                <div className="public-cards-grid public-cards-grid--limited">
                  {cards.map((card) => {
                    const isStory = card.aspect_ratio === 'story';
                    const typeLabel = getTypeLabel(card.quiz_type);
                    const isSynthetic = (card as any).synthetic || !card.image_url;
                    return (
                      <div
                        key={card.id}
                        className="public-card"
                        onClick={() => setModalCard(card)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter') setModalCard(card); }}
                      >
                        <div className={`public-card-media ${isStory ? 'is-story' : 'is-feed'}`} style={(card as any).result || isSynthetic ? { background: '#0a0a0a' } : undefined}>
                          {((card as any).result || isSynthetic) ? (
                            <QuizShareCard
                              id={`p-${card.id}`}
                              variant={card.quiz_type as any}
                              aspect="story"
                              fitContainer
                              displayWidth={200}
                              shareTitle={((card as any).result || (card as any).result)?.shareTitle || card.share_title || ''}
                              shareHook={((card as any).result || (card as any).result)?.shareHook || ''}
                              percent={((card as any).result || (card as any).result)?.percent || 67}
                              typeKey={((card as any).result || (card as any).result)?.typeKey || 'A'}
                              variantKey={((card as any).result || (card as any).result)?.variantKey || card.variant_key}
                              displayName={card.display_name || profile?.nickname || ''}
                              city={profile?.city || undefined}
                              zodiac={profile?.zodiac?.name}
                              zodiacSymbol={profile?.zodiac?.symbol}
                              stats={((card as any).result || (card as any).result)?.stats || []}
                              progressBars={((card as any).result || (card as any).result)?.progressBars}
                            />
                          ) : (
                            <img
                              src={card.image_url}
                              alt={card.share_title || typeLabel}
                              loading="lazy"
                            />
                          )}
                          <div className="public-card-overlay">
                            <span className="public-card-type">{typeLabel}</span>
                          </div>
                        </div>
                        <div className="public-card-meta">
                          <div className="public-card-title" title={card.share_title || ''}>
                            {card.share_title || '—'}
                          </div>
                          <div className="public-card-sub">
                            {isSynthetic ? 'Quiz điền sẵn (mẫu)' : formatDate(card.created_at)} · {isStory ? '9:16 Story' : '3:4 Feed'}
                            {((card as any).total || (card as any).likes) ? (
                              <span style={{ marginLeft: 6, color: '#fcd34d' }}>
                                ❤️ {((card as any).total ?? ((card as any).likes || 0) + ((card as any).thims || 0))}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="public-cards-note">
                Mỗi bài trắc nghiệm chỉ hiển thị thẻ card mới nhất của người đó. Khi có thêm quiz mới, số thẻ sẽ tăng.
              </div>
            </div>

            <div className="public-footer-note">
              Trang này công khai — bất kỳ ai có liên kết đều có thể xem. 
              Mỗi bài trắc nghiệm chỉ hiển thị thẻ card mới nhất. Hồ sơ mẫu có quiz kết quả điền sẵn (A1/A2/A3 + màu ngẫu nhiên).
            </div>
          </>
        )}
      </div>

      {/* Simple Image Modal */}
      {modalCard && (
        <div className="public-modal" onClick={() => setModalCard(null)}>
          <div className="public-modal-content" onClick={e => e.stopPropagation()}>
            <button className="public-modal-close" onClick={() => setModalCard(null)} aria-label={t.close}>✕</button>

            <div className="public-modal-media">
              {(modalCard as any).synthetic && (modalCard as any).result ? (
                <QuizShareCard
                  id={`pm-${modalCard.id}`}
                  variant={modalCard.quiz_type as any}
                  aspect="story"
                  fitContainer
                  displayWidth={360}
                  shareTitle={(modalCard as any).result.shareTitle || modalCard.share_title || ''}
                  shareHook={(modalCard as any).result.shareHook || ''}
                  percent={(modalCard as any).result.percent || 67}
                  typeKey={(modalCard as any).result.typeKey || 'A'}
                  variantKey={(modalCard as any).result.variantKey}
                  displayName={modalCard.display_name || profile?.nickname || ''}
                  city={profile?.city || undefined}
                  zodiac={profile?.zodiac?.name}
                  zodiacSymbol={profile?.zodiac?.symbol}
                  stats={(modalCard as any).result.stats || []}
                  progressBars={(modalCard as any).result.progressBars}
                />
              ) : (
                <img src={modalCard.image_url} alt={modalCard.share_title || ''} />
              )}
            </div>

            <div className="public-modal-info">
              <div>
                <span className="public-modal-type">{getTypeLabel(modalCard.quiz_type)}</span>
                {modalCard.share_title && <span className="public-modal-title"> — {modalCard.share_title}</span>}
              </div>
              <div className="public-modal-sub">
                {formatDate(modalCard.created_at)} · {modalCard.aspect_ratio}
              </div>

              <div className="public-modal-actions">
                <a
                  href={modalCard.image_url}
                  target="_blank"
                  rel="noreferrer"
                  className="public-btn"
                >
                  {t.viewOriginal}
                </a>
                <button onClick={() => setModalCard(null)} className="public-btn">
                  {t.close}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
